import React, { FC, Suspense, useState } from 'react';
import { Icon } from 'src/components/widgets/icon';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Drawer } from 'src/components/widgets/drawer';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import { T } from 'src/shared/helpers';
import type { addMoqRulePanel_AssignProductMutation } from './__generated__/addMoqRulePanel_AssignProductMutation.graphql';
import type { addMoqRulePanel_MoqRulesListQuery } from './__generated__/addMoqRulePanel_MoqRulesListQuery.graphql';
import { PanelLinesSkeleton } from '../common/skeletons';

import {
    EmptyState,
    FooterButton,
    ItemActionSuccess,
    ItemDot,
    ItemName,
    ItemNameAction,
    PanelItem,
    PanelItemSpaced,
    StackedBody,
    StackedFooterButtons
} from '../common/styled';

const AddMoqRulePanelContent: FC<{
    productId: number;
    productRelayId: string;
    existingRuleIds: string[];
    onBack: () => void;
    onAssigned: () => void;
    onCreateNew: () => void;
}> = ({ productId, productRelayId, existingRuleIds, onBack, onAssigned, onCreateNew }) => {
    const [assigning, setAssigning] = useState<string | null>(null);

    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query addMoqRulePanel_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productRelayId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const data = useLazyLoadQuery<addMoqRulePanel_MoqRulesListQuery>(
        graphql`
            query addMoqRulePanel_MoqRulesListQuery {
                moqRules(first: 200) {
                    edges {
                        node {
                            id
                            moqRuleID
                            name
                            printTypes {
                                printTypeID
                            }
                            globalColors
                        }
                    }
                }
            }
        `,
        {}
    );

    const allRules = React.useMemo(() => {
        const edges = data?.moqRules?.edges || [];
        return edges.map((edge) => ({
            id: edge!.node!.id,
            name: edge!.node!.name || '#' + edge!.node!.moqRuleID,
            isAssignedToPrintType: (edge!.node!.printTypes?.length ?? 0) > 0,
            isAssignedToGlobalVariant: (edge!.node!.globalColors?.length ?? 0) > 0
        }));
    }, [data]);

    const [commitAssign] = useMutation<addMoqRulePanel_AssignProductMutation>(graphql`
        mutation addMoqRulePanel_AssignProductMutation($moqRuleId: ID!, $productIds: [Long!]!) {
            assignProductsToMoqRule(moqRuleId: $moqRuleId, productIds: $productIds) {
                result
            }
        }
    `);

    const handleAssign = (rule: { id: string; name: string }) => {
        setAssigning(rule.id);
        commitAssign({
            variables: {
                moqRuleId: rule.id,
                productIds: [productId]
            },
            onCompleted: () => {
                setAssigning(null);
                onAssigned();
            },
            onError: () => {
                setAssigning(null);
            }
        });
    };

    const availableRules = allRules.filter((rule) => !existingRuleIds.includes(rule.id));
    const printTypeAssignedTooltip = T._(
        "This rule is assigned to a printing method and can't be assigned to a product. Either assign that printing method to this product, or change the rule assignment from the tool page.",
        'Admin'
    );
    const globalVariantAssignedTooltip = T._(
        "This rule is assigned to a global variant and can't be assigned to a product. Change the rule assignment from the tool page.",
        'Admin'
    );

    return (
        <Drawer
            isOpen={true}
            isStacked
            onClose={onBack}
            header={{ title: T._('Add MOQ rule', 'Admin'), subtitle: productName }}
            width={580}
            footer={
                <StackedFooterButtons>
                    <FooterButton onClick={onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                </StackedFooterButtons>
            }
        >
            <StackedBody>
                {availableRules.length === 0 && <EmptyState>{T._('All MOQ rules are already assigned', 'Admin')}</EmptyState>}
                {availableRules.map((rule) => {
                    const isDisabled = rule.isAssignedToPrintType || rule.isAssignedToGlobalVariant;
                    const tooltipText = rule.isAssignedToPrintType
                        ? printTypeAssignedTooltip
                        : rule.isAssignedToGlobalVariant
                          ? globalVariantAssignedTooltip
                          : undefined;
                    return (
                        <ZTooltip key={rule.id} title={tooltipText} position='top' addContainer>
                            <PanelItem
                                $clickable={!isDisabled && assigning === null}
                                $disabled={isDisabled}
                                onClick={isDisabled || assigning !== null ? undefined : () => handleAssign(rule)}
                            >
                                <ItemDot $color='#3b82f6' />
                                <ItemName>{rule.name}</ItemName>
                                <ItemActionSuccess>{assigning === rule.id ? T._('Assigning...', 'Admin') : T._('Assign', 'Admin')}</ItemActionSuccess>
                            </PanelItem>
                        </ZTooltip>
                    );
                })}
                <PanelItemSpaced
                    $clickable
                    onClick={() => {
                        onBack();
                        onCreateNew();
                    }}
                >
                    <ItemDot $color='#d1d5db' />
                    <ItemNameAction>
                        <Icon>
                            <PlusIcon />
                        </Icon>
                        {T._('Create new', 'Admin')}
                    </ItemNameAction>
                </PanelItemSpaced>
            </StackedBody>
        </Drawer>
    );
};

export const AddMoqRulePanel: FC<{
    productId: number;
    productRelayId: string;
    existingRuleIds: string[];
    onBack: () => void;
    onAssigned: () => void;
    onCreateNew: () => void;
}> = (props) => {
    return (
        <Suspense
            fallback={
                <Drawer
                    isOpen={true}
                    isStacked
                    onClose={props.onBack}
                    header={{ title: T._('Add MOQ rule', 'Admin'), subtitle: '' }}
                    width={580}
                    footer={
                        <StackedFooterButtons>
                            <FooterButton onClick={props.onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                        </StackedFooterButtons>
                    }
                >
                    <StackedBody>
                        <PanelLinesSkeleton />
                    </StackedBody>
                </Drawer>
            }
        >
            <AddMoqRulePanelContent {...props} />
        </Suspense>
    );
};
